import React, { useEffect, useState } from "react";
import { memberDetailsValidationSchema } from "../../utils/validationSchema";
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import { Button, Form, Row, Col, Container } from "react-bootstrap";
import FormInput from "../../components/ui/FormInput";
// amountToRelease / processingCharge / gst / insuranceAmount store PAISE.
import { toRupees, toPaise } from "../../utils/money";
import { memberDetailsFields, handleFileChange } from "../../utils/utils";
import axios from "axios";
import { API_BASE_URL } from "../../config";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../../components/shared/AuthContext";
import ErrorMessage from "../../components/shared/ErrorMessage";

const MemberDetails = ({
  customerId,
  memberDetailsData,
  setMemberDetailsData,
  handleNext,
  type,
  locked = false,
  role = "user", // Default role as "user", can be passed as "admin"
}) => {
  const navigate = useNavigate();
  const { data } = useAuth();
  const [errorMsg, setErrorMsg] = useState("");

  const {
    register,
    handleSubmit,
    formState: { errors },
    setValue,
    watch,
    control,
  } = useForm({
    resolver: yupResolver(memberDetailsValidationSchema),
    defaultValues: memberDetailsData,
  });

  const [filePreviews, setFilePreviews] = useState({});
  const [existingFiles, setExistingFiles] = useState({});
  const [fieldsData, setFieldsData] = useState([]);

  // Fields that should not be editable
  const nonEditableFields = [
    "dataEntryOperatorStatusUpdatedAt",
    "branchManagerStatus",
    "branchManagerMessage",
    "branchManagerStatusUpdatedAt",
    "adminStatus",
    "adminMessage",
    "adminStatusUpdatedAt",
    "loanId",
  ];

  useEffect(() => {
    const fetchFieldsData = async () => {
      const data = await memberDetailsFields();
      setFieldsData(data);
    };

    fetchFieldsData();
  }, []);

  useEffect(() => {
    if (memberDetailsData) {
      // These four columns store PAISE (money-to-paise-bigint-stage2) and are
      // rendered read-only in View mode, so they need converting on the way in.
      // securityDeposit is deliberately absent — it is int(11) and was never
      // converted, so dividing it would break a correct value.
      const PAISE_FIELDS = ["amountToRelease", "processingCharge", "gst", "insuranceAmount"];

      Object.keys(memberDetailsData).forEach((key) => {
        const raw = memberDetailsData[key];
        setValue(key, PAISE_FIELDS.includes(key) && raw != null ? toRupees(raw) : raw);
        if (key.endsWith("Image") && memberDetailsData[key]) {
          setExistingFiles((prev) => ({
            ...prev,
            [key]: `${API_BASE_URL}/images/${memberDetailsData[key]}`,
          }));
        }
      });
    }
  }, [memberDetailsData, setValue]);

  const dateOfBirth = watch("dateOfBirth");

  useEffect(() => {
    if (dateOfBirth) {
      const age = calculateAge(dateOfBirth);
      setValue("age", age, { shouldValidate: true });
    }
  }, [dateOfBirth, setValue]);

  const calculateAge = (dateOfBirth) => {
    let birthDate;
    if (dateOfBirth && typeof dateOfBirth === "string") {
      const cleaned = dateOfBirth.trim();
      if (cleaned.includes("-")) {
        const parts = cleaned.split("-");
        if (parts[0].length === 4) {
          // YYYY-MM-DD
          birthDate = new Date(cleaned);
        } else {
          // DD-MM-YYYY
          const [day, month, year] = parts;
          birthDate = new Date(`${year}-${month}-${day}`);
        }
      } else if (cleaned.includes(".")) {
        // DD.MM.YYYY
        const [day, month, year] = cleaned.split(".");
        birthDate = new Date(`${year}-${month}-${day}`);
      } else {
        birthDate = new Date(cleaned);
      }
    } else {
      birthDate = new Date(dateOfBirth);
    }

    if (!birthDate || isNaN(birthDate.getTime())) {
      return "";
    }

    const currentDate = new Date();
    const age = currentDate.getFullYear() - birthDate.getFullYear();
    const month = currentDate.getMonth() - birthDate.getMonth();
    if (
      month < 0 ||
      (month === 0 && currentDate.getDate() < birthDate.getDate())
    ) {
      return age - 1;
    }
    return age;
  };

  const onSubmit = async (submittedData) => {
    // Get branchId from auth data
    const branchId = data?.branchId || null;
    console.log("Using branchId:", branchId);

    // RUPEES -> PAISE for the four columns converted on load above. onSubmit
    // spreads the whole form into the payload, so without this the rupee values
    // shown in View mode would be written straight back into paise columns and
    // divide the loan by 100 on every save.
    //
    // Only fields actually PRESENT are touched: these four are read-only and
    // omitted outside View mode, and writing undefined would blank them.
    const PAISE_FIELDS = ["amountToRelease", "processingCharge", "gst", "insuranceAmount"];
    const moneyAsPaise = {};
    PAISE_FIELDS.forEach((k) => {
      if (submittedData[k] !== undefined && submittedData[k] !== null && submittedData[k] !== "") {
        moneyAsPaise[k] = toPaise(submittedData[k]);
      }
    });

    const payload = {
      ...submittedData,
      ...moneyAsPaise,
      model: "member_details",
      type,
      customerId,
      branchId, // Add branchId to payload
    };

    if (type === "Edit") {
      // `id` is taken from the LOADED RECORD, not the form. It is not in
      // memberDetailsFields(), so react-hook-form never returns it in
      // submittedData — setValue() on an unregistered name is dropped — and the
      // payload went out with no id at all. addDetails.js:785 then refused it
      // with 400 "Update requires a valid ID.", correctly: an Edit with no id
      // would otherwise UPDATE every row. Same fix as VerificationDetails.js:219.
      const rowId = memberDetailsData?.id;
      if (!rowId) {
        // Unreachable once the parent sends the no-row case to Add. Kept so that
        // an id missing for any other reason reads as a sentence here rather
        // than a raw 400 from the server.
        setErrorMsg(
          "These member details have not been saved yet, so there is nothing to update."
        );
        return;
      }
      payload.id = rowId;
    }

    try {
      const response = await axios.post(
        `${API_BASE_URL}/api/addDetails`,
        payload
      );

      if (response.status === 201) {
        setMemberDetailsData(response.data.savedData);
        console.log("Member Details added successfully: ", response.data);
        if (type === "Add") {
          handleNext();
        }
        if (type === "Edit") {
          const savedMemberId = response.data.savedData?.id || memberDetailsData?.id;
          navigate("/addEditBusinessLoan", {
            state: { type: "View", stepNo: 2, memberId: savedMemberId, customerId },
          });
        }
        setErrorMsg("");
      } else {
        setErrorMsg(response.data.error || "Error adding member details");
      }
    } catch (error) {
      console.error("Error Adding Member Details: ", error);
      const msg = error.response?.data?.error || error.response?.data?.message || error.message || "Error adding member details";
      setErrorMsg(msg);
    }
  };

  const onError = (errors) => {
    const errorMessages = Object.keys(errors).reduce((acc, key) => {
      acc[key] = errors[key].message;
      return acc;
    }, {});

    console.log("Form errors:", errorMessages);
  };

  const handleEditClick = () => {
    navigate("/addEditBusinessLoan", {
      state: { type: "Edit", stepNo: 2, customerId },
    });
  };

  // Disbursement outcomes, not inputs. ActionButtons.jsx writes all ten when a
  // loan is disbursed; nobody types them here, and every one is readOnly in
  // utils.js. Before disbursement they are empty or zero, so in Add and Edit
  // they are ten boxes that cannot be filled and mean nothing — shown only in
  // View, where they report what actually happened.
  const DISBURSEMENT_FIELDS = [
    "processingCharge",
    "isProcessingChargePaid",
    "gst",
    "isGstPaid",
    "securityDeposit",
    "isSecurityDepositPaid",
    "isLoanInsured",
    "insuranceAmount",
    "isInsuranceAmountPaid",
    "amountToRelease",
  ];

  const visibleFields = fieldsData.filter(
    (field) => type === "View" || !DISBURSEMENT_FIELDS.includes(field.name)
  );

  // Determine if a field should be read-only based on type, role, and nonEditableFields
  const isFieldReadOnly = (fieldName) => {
    if (type === "View") return true; // All fields read-only in View mode
    if (nonEditableFields.includes(fieldName)) {
      // If admin, allow editing admin-related fields
      if (role === "admin" && fieldName.startsWith("admin")) {
        return false;
      }
      return true; // Non-editable fields remain read-only
    }
    return false; // Editable by default
  };

  return (
    <Container className="mt-2 mt-md-4">
      <div className="d-flex justify-content-center align-items-center gap-3 mb-3">
        <h2 className="mb-0 text-center" style={{ fontSize: 'clamp(1.1rem, 5vw, 1.5rem)', fontWeight: 700 }}>Member Details</h2>
        {/* Hidden once the loan is disbursed. type="View" alone would still
            render this button, and it navigates back in as type="Edit". */}
        {!locked && type === "View" && (
          <Button className="mt-2 btn-hover" variant="primary" onClick={handleEditClick}>Edit</Button>
        )}
      </div>
      <ErrorMessage message={errorMsg} />
      <Form onSubmit={handleSubmit(onSubmit, onError)}>
        <Row>
          {visibleFields.map((field, index) => (
            <Col
              xs={12}
              md={6}
              key={index}
              className="mb-3"
            >
              <FormInput
                label={field.label}
                type={field.type}
                name={field.name}
                register={register}
                watch={watch}
                control={control}
                errors={errors}
                options={field.options || []}
                readOnly={isFieldReadOnly(field.name) || field.readOnly}
                handleFileChange={(e) =>
                  handleFileChange(e, field.name, setFilePreviews)
                }
                existingFileUrl={existingFiles[field.name] || ""}
                filePreview={filePreviews[field.name]?.previewUrl || ""}
                required={field.required}
              />
            </Col>
          ))}
        </Row>

        <div className="text-center">
          {type === "Add" && (
            <Button variant="success" type="submit" className="mt-3 mx-2">
              Submit
            </Button>
          )}
          {type === "Edit" && (
            <>
              <Button variant="success" type="submit" className="mt-3 mx-2">
                Update
              </Button>
              <Button
                variant="warning"
                className="mt-3 mx-2"
                onClick={() =>
                  navigate("/addEditBusinessLoan", {
                    state: { type: "View", stepNo: 2, customerId },
                  })
                }
              >
                Cancel
              </Button>
            </>
          )}
        </div>
      </Form>
    </Container>
  );
};

export default MemberDetails;